import { htmlSafe } from '@ember/template';
import { CheckInStatusEnum } from 'garaje/services/live-map';

// SPACEQ-131 — live-map onsite presence dot. This file owns ALL presence-dot
// logic; the only consumers are resource-map's `getLiveMapIconForDeskFeature`
// and `currentUserHtml` (flag-gated via `live-map-presence-indicator-web` in
// `_shouldShowPresenceDot`). Removing the feature = delete this file + its
// test and unwrap the call sites.
//
// iOS parity (MapIconView.swift): on the reference 36pt marker the presence
// dot is 14pt with a 2pt white stroke, anchored to the avatar's bottom-right
// corner. Expressing both as ratios of markerSize means the dot scales
// identically in legacy and custom marker-sizing modes — the mode is already
// folded into markerSize before the icon HTML is built, and zoom scaling is
// a CSS transform on the whole marker.
//
// No stroke floor, on purpose: iOS removed its fixed-inset minimum (AMP-2794,
// StatusMapIconView's `statusIndicatorFillDiameterRatio`) because any fixed
// minimum eats the green fill on small markers — with border-box sizing a 1px
// floor leaves a 10px base marker only ~49% green vs the constant 10/14 fill
// iOS keeps at every size.
const PRESENCE_DOT_DIAMETER_RATIO = 14 / 36;
const PRESENCE_DOT_STROKE_RATIO = 2 / 36;

function roundToHundredth(value) {
  return Math.round(value * 100) / 100;
}

export function getPresenceDotMetrics(markerSize) {
  return {
    dotSize: roundToHundredth(markerSize * PRESENCE_DOT_DIAMETER_RATIO),
    strokeWidth: roundToHundredth(markerSize * PRESENCE_DOT_STROKE_RATIO),
  };
}

// Static styles (position, colors, border-radius) live in the
// `.presence-indicator` rule in _resource-map.scss — its own class on
// purpose, NOT `rounded-full`: the glyph-hide sweep in resource-map hides
// children of every `.rounded-full` node below the small-marker threshold,
// and the dot must stay out of it. Only markerSize-derived values are
// inline.
export function getPresenceDotHtml(markerSize) {
  const { dotSize, strokeWidth } = getPresenceDotMetrics(markerSize);

  return `<div
    data-test-presence-indicator
    class="presence-indicator"
    style="width:${dotSize}px;height:${dotSize}px;border-width:${strokeWidth}px;"
  ></div>`;
}

// Wraps a circular marker shell with a presence dot. The dot is a SIBLING of
// the shell inside a relative container (the shell's own `overflow-hidden`
// circular clip would cut a corner-anchored child). The wrapper has the same
// markerSize footprint as the shell, so `_wrapInMarkerScale`'s pixel-anchored
// transform-origin and the DivIcon anchor math are unaffected. `flex-shrink:0`
// keeps the wrapper from being squashed when it sits next to the nowrap label
// inside `currentUserHtml`'s fixed-width flex row.
export function withPresenceIndicator(shellHtml, markerSize) {
  if (!shellHtml || shellHtml.toString() === '') {
    return shellHtml;
  }

  return htmlSafe(
    `<div style="position:relative;flex-shrink:0;width:${markerSize}px;height:${markerSize}px;">${shellHtml.toString()}${getPresenceDotHtml(
      markerSize,
    )}</div>`,
  );
}

export function isResourceCheckedIn(resourceState) {
  return resourceState?.status?.checkInStatus?.status === CheckInStatusEnum.CHECKED_IN;
}
